import { useQuery } from '@apollo/client/react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
    ICurrentUserFragment,
    IRole,
    IUserProfileFragment,
    CurrentUserDocument,
    OffersSettingsDocument,
} from 'graphql/types';
import { useFeature } from 'common/hooks/useFeature';
import { useUserProfileTabs } from 'user/hooks';
import { Alert } from 'common/components/Alert';
import { Button } from 'common/components/Button';
import { Link } from 'common/components/Link';
import { useRouteMatch } from 'route/hooks/useRouteMatch';

import { UserTabsMenu } from '../UserTabsMenu';

interface IProps {
    canUsePortfolioOffers?: boolean;
    canUseSkills?: boolean;
    isCurrentUser?: boolean;
    user: IUserProfileFragment | ICurrentUserFragment;
    hideOffersSection?: boolean;
    hideMeetingsSection?: boolean;
}

export const UserProfile = ({
    canUsePortfolioOffers,
    canUseSkills,
    isCurrentUser,
    hideOffersSection,
    hideMeetingsSection,
    user,
}: IProps) => {
    const [translate] = useTranslation();
    const { tabs, Section } = useUserProfileTabs(
        user.id,
        isCurrentUser,
        canUsePortfolioOffers,
        hideOffersSection,
        hideMeetingsSection,
        canUseSkills
    );
    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const { data: offersSettingsData } = useQuery(OffersSettingsDocument);
    const { showOffers: isOffersPublished } = useFeature(undefined, true);

    const { publishTraining, publishElearning, publishMeetingWebinar } =
        offersSettingsData?.offersSettings || {};

    const isTrainingOffersPage = !!useRouteMatch('PORTFOLIO_OFFERS');
    const isTrainingOffersUserPage = !!useRouteMatch('USER_OFFERS');
    const isMeetingsPage = !!useRouteMatch('PORTFOLIO_MEETINGS');
    const isMeetingsUserPage = !!useRouteMatch('USER_MEETINGS');

    // The portfolio offers page which shows training and courses
    const isOffersPage = isTrainingOffersPage || isTrainingOffersUserPage;
    // The portfolio meetings page which shows meetings and webinars
    const isPortfolioMeetingsPage = isMeetingsPage || isMeetingsUserPage;
    // Every offers related portfolio page
    const isPortfolioOffersPage = isOffersPage || isPortfolioMeetingsPage;

    const { roles } = currentUserData?.currentUser || {};
    const currentUserIsManager = roles?.includes(IRole.Manager);

    let showAlert = false;

    if (currentUserIsManager) {
        // If all offers are not published
        if (isPortfolioOffersPage && !isOffersPublished) {
            showAlert = true;
        } else if (
            // If individual offer types are not published
            (isOffersPage && !publishTraining && !publishElearning) ||
            (isPortfolioMeetingsPage && !publishMeetingWebinar)
        ) {
            showAlert = true;
        }
    }

    return (
        <>
            <Box>
                <Box sx={{ mb: 2 }}>
                    <UserTabsMenu tabs={tabs} />
                </Box>

                {showAlert && (
                    <Alert
                        action={
                            <Button
                                color="inherit"
                                component={Link}
                                size="small"
                                sx={{ fontWeight: 'bold', mr: 1 }}
                                to="OFFER_SETTINGS_EDIT"
                            >
                                {translate('settings')}
                            </Button>
                        }
                        severity="error"
                        sx={{ mb: 2 }}
                    >
                        {translate(
                            `${isOffersPage ? 'portfolioOffersPage' : 'portfolioMeetingsPage'}.notPublishedMessage`
                        )}
                    </Alert>
                )}

                {Section && (
                    <Box>
                        <Section {...user} withEditActions={isCurrentUser} />
                    </Box>
                )}
            </Box>
        </>
    );
};
